import React, { useState } from 'react';
import { Layer } from '../../types';
import './LayerList.css';

interface LayerListProps {
  filteredLayers: Layer[];
  toggleImagery: (layerId: string) => void;
  toggleDetections: (layerId: string) => void;
}

interface MetadataPopupProps {
  layer: Layer;
  onClose: () => void;
}

const MetadataPopup: React.FC<MetadataPopupProps> = ({ layer, onClose }) => {
  return (
    <div className="metadata-popup-overlay" onClick={onClose}>
      <div className="metadata-popup" onClick={(e) => e.stopPropagation()}>
        <div className="metadata-popup-header">
          <h4>Dataset Metadata</h4>
          <button
            className="metadata-close-btn"
            onClick={onClose}
            aria-label="Close metadata"
          >
            ×
          </button>
        </div>

        <div className="metadata-content">
          <div className="metadata-section">
            <h5>Dataset Information</h5>
            <div className="metadata-item">
              <strong>Name:</strong> {layer.name}
            </div>
            <div className="metadata-item">
              <strong>Dataset ID:</strong> {layer.datasetId}
            </div>
            <div className="metadata-item">
              <strong>Field:</strong> {layer.field}
            </div>
            <div className="metadata-item">
              <strong>Research Group:</strong> {layer.group}
            </div>
          </div>

          <div className="metadata-section">
            <h5>Collection Details</h5>
            <div className="metadata-item">
              <strong>Date Collected:</strong> {layer.dateCollected}
            </div>
            <div className="metadata-item">
              <strong>Burial Time:</strong> {layer.burialTime}
            </div>
            <div className="metadata-item">
              <strong>Collection Method:</strong> {layer.collectionMethod}
            </div>
          </div>

          <div className="metadata-section">
            <h5>Technical Specifications</h5>
            <div className="metadata-item">
              <strong>Sensor Type:</strong> {layer.sensorType}
            </div>
            <div className="metadata-item">
              <strong>Sensor:</strong> {layer.sensor}
            </div>
            <div className="metadata-item">
              <strong>Platform:</strong> {layer.platform}
            </div>
            {(
              <div className="metadata-item">
                <strong>AGL Height:</strong> {layer.agl} m
              </div>
            )}
          </div>

          <div className="metadata-section">
            <h5>Data Availability</h5>
            <div className="metadata-item">
              <strong>Has Imagery:</strong> {layer.hasImagery ? 'Yes' : 'No'}
            </div>
            <div className="metadata-item">
              <strong>Has Detections:</strong> {layer.hasDetections ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LayerList: React.FC<LayerListProps> = ({
  filteredLayers,
  toggleImagery,
  toggleDetections
}) => {
  const [selectedMetadataLayer, setSelectedMetadataLayer] = useState<Layer | null>(null);

  const showMetadata = (layer: Layer) => {
    setSelectedMetadataLayer(layer);
  };

  const hideMetadata = () => {
    setSelectedMetadataLayer(null);
  };

  return (
    <div className="layer-list">
      <h3>Available Datasets ({filteredLayers.length})</h3>
      {filteredLayers.length === 0 ? (
        <p>No datasets match the current filters</p>
      ) : (
        filteredLayers.map(layer => (
          <div key={layer.id} className="dataset-item">
            <div className="dataset-header">
              <div className="dataset-name-container">
                <h4 className="dataset-name">
                  {layer.name}
                  <span className={`burial-badge burial-${layer.burialTime.toLowerCase()}`}>
                    {layer.burialTime}
                  </span>
                </h4>
                <button
                  className="info-icon-btn"
                  onClick={() => showMetadata(layer)}
                  aria-label={`Show metadata for ${layer.name}`}
                  title="View dataset metadata"
                >
                  ℹ️
                </button>
              </div>
              {/* <div className="dataset-meta"> */}
              {/*   <span className="dataset-group">{layer.group}</span> */}
              {/*   <span className="dataset-date">{layer.dateCollected}</span> */}
              {/* </div> */}
            </div>

            <div className="data-toggles">
              {layer.hasImagery ? (
                <div className="toggle-item">
                  <input
                    type="checkbox"
                    id={`imagery-${layer.id}`}
                    checked={layer.showImagery}
                    onChange={() => toggleImagery(layer.id)}
                  />
                  <label htmlFor={`imagery-${layer.id}`} className="toggle-label">
                    Imagery
                  </label>
                </div>
              ) : (
                <div className="toggle-item disabled">
                  <input
                    type="checkbox"
                    id={`imagery-${layer.id}`}
                    checked={false}
                    disabled
                  />
                  <label htmlFor={`imagery-${layer.id}`} className="toggle-label disabled">
                    Imagery
                  </label>
                </div>
              )}

              {layer.hasDetections ? (
                <div className="toggle-item">
                  <input
                    type="checkbox"
                    id={`detections-${layer.id}`}
                    checked={layer.showDetections}
                    onChange={() => toggleDetections(layer.id)}
                  />
                  <label htmlFor={`detections-${layer.id}`} className="toggle-label">
                    Detections
                  </label>
                </div>
              ) : (
                <div className="toggle-item disabled">
                  <input
                    type="checkbox"
                    id={`detections-${layer.id}`}
                    checked={false}
                    disabled
                  />
                  <label htmlFor={`detections-${layer.id}`} className="toggle-label disabled">
                    Detections (not available)
                  </label>
                </div>
              )}
            </div>

            {/* <div className="dataset-details"> */}
            {/*   <span className="sensor-info">{layer.sensorType}</span> */}
            {/*   {layer.agl && ( */}
            {/*     <span className="agl-info">AGL: {layer.agl}m</span> */}
            {/*   )} */}
            {/* </div> */}
          </div>
        ))
      )}

      {selectedMetadataLayer && (
        <MetadataPopup
          layer={selectedMetadataLayer}
          onClose={hideMetadata}
        />
      )}
    </div>
  );
};

export default LayerList;
